import React, { useState, useEffect, useRef } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import { useAuth } from '../AuthContext';
import api from '../api';

const LINK_FIELDS = [
  { key: 'first_name',             title: 'First Name',               example: 'John' },
  { key: 'last_name',              title: 'Last Name',                example: 'Smith' },
  { key: 'name',                   title: 'Full Name',                example: 'John Smith' },
  { key: 'email',                  title: 'Email',                    example: 'example@email.com' },
  { key: 'phone',                  title: 'Phone',                    example: '(123) 456-7890' },
  { key: 'state',                  title: 'State',                    example: 'AZ' },
  { key: 'coverage_amount',        title: 'Coverage Amount',          example: '500000' },
  { key: 'height_ft',              title: 'Height (ft)',              example: '5' },
  { key: 'height_in',              title: 'Height (in)',              example: '10' },
  { key: 'weight',                 title: 'Weight (lbs)',             example: '180' },
  { key: 'gender',                 title: 'Gender',                   example: 'Male' },
  { key: 'dob',                    title: 'Date of Birth',            example: '1970-03-15' },
  { key: 'health',                 title: 'Health Class',             example: 'PP/P/R' },
  { key: 'nicotine',               title: 'Nicotine Use',             example: 'never/currently/recently' },
  { key: 'duration',               title: 'Duration (Term Only)',     example: '10/20/30' },
  { key: 'r',                      title: 'Referring Page',           example: 'https://yoursite.com' },
  { key: 'results_modal_override', title: '*Bypass Name/Email/Phone', example: 'hide/show' },
  { key: 'skip_confirmation',      title: '**Skip Confirmation',      example: '0/1' },
];

const STATIC_ENGINES = [
  { id: 1,  title: 'Term Life Insurance',               url_slug: 'term-life' },
  { id: 2,  title: 'No Medical Exam Life Insurance',    url_slug: 'no-medical-exam' },
  { id: 4,  title: 'Universal Life Insurance',          url_slug: 'universal-life' },
  { id: 5,  title: 'Return of Premium Life Insurance',  url_slug: 'return-of-premium' },
  { id: 6,  title: 'Accidental Death Benefit Insurance',url_slug: 'accidental-death' },
  { id: 7,  title: 'Guaranteed Issue Life Insurance',   url_slug: 'guaranteed-issue' },
  { id: 9,  title: 'Final Expense Insurance',           url_slug: 'final-expense' },
  { id: 10, title: 'Living Benefits Insurance',         url_slug: 'living-benefits' },
  { id: 12, title: 'Instant Issue Life Insurance',      url_slug: 'instant-issue' },
  { id: 13, title: "Children's Whole Life Insurance",   url_slug: 'childrens-whole-life' },
  { id: 14, title: 'Agentless Term Life Insurance',     url_slug: 'agentless-term' },
  { id: 17, title: 'Whole Life Insurance',              url_slug: 'whole-life' },
  { id: 18, title: 'Custom',                            url_slug: 'custom' },
];

const ENGINE_ORDER = [1, 9, 4, 18, 2, 5, 6, 7, 10, 12, 13, 14, 17];

function CodeSnippet({ label, description, code, preview, previewLink }) {
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
        <div>
          <span style={{ fontWeight: '600', fontSize: '13px', color: '#0F172A' }}>{label}</span>
          {description && <span style={{ fontSize: '12px', color: '#64748B', marginLeft: '8px' }}>{description}</span>}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {previewLink ? (
            <a href={previewLink} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 12px', background: 'white', border: '1px solid #DDE7F1', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 150ms', textDecoration: 'none' }}>
              <i className="fas fa-external-link-alt" style={{ fontSize: '11px' }}></i>
              Preview
            </a>
          ) : (
            <button onClick={() => setShowCode(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 12px', background: showCode ? '#F1F5F9' : 'white', border: '1px solid #DDE7F1', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer', transition: 'all 150ms' }}>
              <i className={`fas ${showCode ? 'fa-eye' : 'fa-code'}`} style={{ fontSize: '11px' }}></i>
              {showCode ? 'Preview' : 'View Code'}
            </button>
          )}
          <button onClick={copy} style={{ display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 12px', background: copied ? '#F0FDF4' : 'white', border: `1px solid ${copied ? '#BBF7D0' : '#DDE7F1'}`, borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: copied ? '#16A34A' : '#2D87F0', cursor: 'pointer', transition: 'all 150ms' }}>
            <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} style={{ fontSize: '11px' }}></i>
            {copied ? 'Copied!' : 'Copy HTML'}
          </button>
        </div>
      </div>
      {previewLink ? (
        <pre style={{
          background: '#FFFFFF', color: '#334155', border: '1px solid #DDE7F1',
          borderRadius: '8px', padding: '16px', fontSize: '12px', lineHeight: '1.6',
          fontFamily: "'Fira Code', 'Consolas', monospace", overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
        }}><code>{code}</code></pre>
      ) : showCode ? (
        <pre style={{
          background: '#1E293B', color: '#E2E8F0', border: '1px solid #334155',
          borderRadius: '8px', padding: '16px', fontSize: '12px', lineHeight: '1.6',
          fontFamily: "'Fira Code', 'Consolas', monospace", overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0,
        }}><code>{code}</code></pre>
      ) : (
        <div style={{ 
          background: '#F8FAFC', border: '1px solid #DDE7F1', borderRadius: '8px', 
          padding: '20px', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {preview}
        </div>
      )}
    </div>
  );
}

export default function LinkBuilderPage() {
  const { brand, isIframe } = useAuth();
  const [baseUrl, setBaseUrl]           = useState('https://demo.quoteplicity.com');
  const [selectedEngine, setSelectedEngine] = useState(null);
  const [paramValues, setParamValues]   = useState({});
  const [activeParams, setActiveParams] = useState(() => LINK_FIELDS.map(f => f.key));
  const [loading, setLoading]           = useState(true);
  const [showVideo, setShowVideo]       = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => !!localStorage.getItem('qp_help_dismissed_linkbuilder')
  );

  const enginesMap = {};
  STATIC_ENGINES.forEach(e => { enginesMap[e.id] = e; });

  useEffect(() => {
    if (brand) {
      const subdomain = brand.subdomain_alias || brand.subdomain || 'demo';
      setBaseUrl(brand.domain ? `https://${brand.domain}` : `https://${subdomain}.quoteplicity.com`);
    } else if (!isIframe) {
      api.get('/link-builder').then(res => {
        setBaseUrl(res.data.base_url || 'https://demo.quoteplicity.com');
      }).catch(console.error);
    }
    const first = ENGINE_ORDER.map(id => enginesMap[id]).find(Boolean);
    if (first) setSelectedEngine(first);
    setLoading(false);
  }, [brand, isIframe]);

  const buildLink = (withParams = true) => {
    if (!selectedEngine) return '';
    let url = `${baseUrl}/quote/${selectedEngine.url_slug}`;
    if (withParams && activeParams.length > 0) {
      const parts = [];
      LINK_FIELDS.forEach(f => {
        if (!activeParams.includes(f.key)) return;
        const rawVal = paramValues[f.key] || '';
        parts.push(`${f.key}=${rawVal}`);
      });
      if (parts.length) url += '?' + parts.join('&');
    }
    return url;
  };

  const toggleParam = (key) =>
    setActiveParams(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const orderedEngines = ENGINE_ORDER.map(id => enginesMap[id]).filter(Boolean);
  const link = buildLink();
  const linkSimple = buildLink(false);

  // Debounced link — applies to BOTH the iframe preview and the text/button/URL snippets.
  // Avoids rebuilding code blocks and reloading the iframe on every keystroke; updates ~800ms
  // after the user pauses typing, or immediately on blur (see input onBlur handler below).
  const [debouncedLink, setDebouncedLink] = useState(link);
  const debounceTimer = useRef(null);
  useEffect(() => {
    debounceTimer.current = setTimeout(() => setDebouncedLink(link), 800);
    return () => clearTimeout(debounceTimer.current);
  }, [link]);
  // Commit immediately when a prefill input loses focus
  const commitNow = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setDebouncedLink(link);
  };

  if (loading) {
    return <ProtectedLayout><div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}><div className="skeleton" style={{ height: '400px', borderRadius: '12px' }}></div></div></ProtectedLayout>;
  }

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-hammer" style={{ color: '#2D87F0', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Link Builder</h1>
        </div>

        {/* Banner */}
        {!bannerDismissed && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', padding: '14px 18px', background: 'white', border: '1px solid #DDE7F1', borderLeft: '4px solid #2D87F0', borderRadius: '10px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: '200px' }}>
              <i className="fas fa-graduation-cap" style={{ color: '#2D87F0', fontSize: '20px', marginTop: '2px', flexShrink: 0 }}></i>
              <div>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#0F172A', marginBottom: '2px' }}>Setup Guide: Link Builder</div>
                <div style={{ fontSize: '13px', color: '#64748B' }}>Select an insurance type, toggle parameters on, then copy the generated HTML for your site.</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => setShowVideo(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'white', background: showVideo ? '#1D6FD8' : '#2D87F0', border: 'none', cursor: 'pointer', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                <i className={`fas ${showVideo ? 'fa-times' : 'fa-play'}`} style={{ fontSize: '11px' }}></i>
                {showVideo ? 'Close Video' : 'Watch Video'}
              </button>
              <a href="https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/kY9eB3nHLWJnUYXjGmcU?preview=true" target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2D87F0', background: '#EFF6FF', border: '1px solid #BFDBFE', textDecoration: 'none', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
                <i className="fas fa-file-lines" style={{ fontSize: '11px' }}></i> Read Article
              </a>
              <button onClick={() => { localStorage.setItem('qp_help_dismissed_linkbuilder', '1'); setBannerDismissed(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', padding: '4px', lineHeight: 1 }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
        )}

        {/* Video panel */}
        {showVideo && (
          <div className="portal-card" style={{ overflow: 'hidden', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-play-circle" style={{ color: '#2D87F0', fontSize: '16px' }}></i>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>Setup Guide: Link Builder</span>
              </div>
              <button onClick={() => setShowVideo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}><i className="fas fa-times"></i></button>
            </div>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              <iframe
                src="https://embed.app.guidde.com/playbooks/iFS7JSbtpqWtnJjcLNypdx?mode=videoOnly"
                title="Generate Custom Quote Links Using Quoteplicity Link Builder"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: '10px' }}
                referrerPolicy="unsafe-url"
                allow="clipboard-write"
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Step 1 — Select insurance type */}
        <div className="portal-card" style={{ padding: '20px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2D87F0', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</span>
            <span style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>Select Insurance Type</span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <select
                value={selectedEngine?.id || ''}
                onChange={e => setSelectedEngine(orderedEngines.find(eng => String(eng.id) === e.target.value))}
                className="input-field"
                style={{ appearance: 'none', cursor: 'pointer', fontSize: '15px', height: '46px', paddingRight: '36px', fontWeight: '500' }}
              >
                {orderedEngines.map(eng => (
                  <option key={eng.id} value={eng.id}>{eng.title}</option>
                ))}
              </select>
              <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '13px', pointerEvents: 'none' }}></i>
            </div>
            {selectedEngine && (
              <a href={linkSimple} target="_blank" rel="noreferrer"
                style={{ height: '46px', width: '46px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', color: '#2D87F0', textDecoration: 'none', fontSize: '16px', transition: 'background 150ms' }}
                title="Open in new tab"
                onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'}
                onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}
              >
                <i className="fas fa-external-link-alt"></i>
              </a>
            )}
          </div>
        </div>

        {/* Step 2 — Pre-fill parameters */}
        <div className="portal-card" style={{ overflow: 'hidden', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2D87F0', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>2</span>
              <span style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>Pre-fill Parameters</span>
              {activeParams.length > 0 && (
                <span style={{ background: '#2D87F0', color: 'white', borderRadius: '999px', fontSize: '11px', fontWeight: '700', padding: '1px 8px' }}>{activeParams.length} active</span>
              )}
            </div>
            <span style={{ fontSize: '12px', color: '#64748B' }}>Toggle parameters to include them in the link</span>
          </div>
          <div style={{ padding: '12px 20px 4px' }}>
            {/* 2-column grid of parameter rows */}
            <div style={{ border: '1px solid #DDE7F1', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#F8FAFC', borderBottom: '1px solid #DDE7F1' }}>
                {['Parameter — Field — Value — On', 'Parameter — Field — Value — On'].map((_, ci) => (
                  <div key={ci} style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 130px) 1fr 32px', gap: '4px', padding: '7px 10px', borderLeft: ci === 1 ? '1px solid #DDE7F1' : 'none' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Field</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Value</div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>On</div>
                  </div>
                ))}
              </div>
              {Array.from({ length: Math.ceil(LINK_FIELDS.length / 2) }, (_, rowIdx) => {
                const left  = LINK_FIELDS[rowIdx * 2];
                const right = LINK_FIELDS[rowIdx * 2 + 1];
                return (
                  <div key={rowIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: rowIdx < Math.ceil(LINK_FIELDS.length / 2) - 1 ? '1px solid #F8FAFC' : 'none' }}>
                    {[left, right].map((field, ci) => field ? (
                      <div key={field.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 130px) 1fr 32px', gap: '4px', padding: '5px 10px', alignItems: 'center', borderLeft: ci === 1 ? '1px solid #F1F5F9' : 'none', background: activeParams.includes(field.key) ? '#FAFBFF' : 'white' }}>
                        <div style={{ fontSize: '12px', fontWeight: activeParams.includes(field.key) ? '600' : '400', color: activeParams.includes(field.key) ? '#2D87F0' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={field.title}>{field.title}</div>
                        <input className="input-field" value={paramValues[field.key] || ''}
                          onChange={e => setParamValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          onBlur={commitNow}
                          placeholder={field.example}
                          style={{ fontSize: '12px', height: '28px', padding: '0 6px', minWidth: 0 }} />
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <button onClick={() => toggleParam(field.key)}
                            style={{ width: '30px', height: '18px', borderRadius: '999px', background: activeParams.includes(field.key) ? '#EFF6FF' : '#F1F5F9', border: `1px solid ${activeParams.includes(field.key) ? '#2D87F0' : '#DDE7F1'}`, position: 'relative', cursor: 'pointer', padding: 0, overflow: 'hidden', transition: 'all 150ms', flexShrink: 0 }}>
                            <span style={{ position: 'absolute', top: '50%', left: activeParams.includes(field.key) ? 'calc(100% - 15px)' : '2px', transform: 'translateY(-50%)', width: '12px', height: '12px', borderRadius: '50%', background: activeParams.includes(field.key) ? '#2D87F0' : '#94A3B8', transition: 'left 200ms, background 150ms' }}></span>
                          </button>
                        </div>
                      </div>
                    ) : <div key={`empty-${ci}`} style={{ borderLeft: ci === 1 ? '1px solid #F1F5F9' : 'none', background: 'white' }} />)}
                  </div>
                );
              })}
            </div>
            <p style={{ fontSize: '11px', color: '#94A3B8', margin: '6px 0 12px' }}>* Overrides default brand setting &nbsp;·&nbsp; ** Skips the data confirmation screen</p>
          </div>
        </div>

        {/* Step 3 — Copy HTML for Your Site */}
        {selectedEngine && (
          <div className="portal-card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2D87F0', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>3</span>
              <span style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>Copy HTML for Your Site</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <CodeSnippet
                label="Text Link"
                description="A simple hyperlink — paste anywhere in your page content."
                code={`<a href="${debouncedLink}" target="_blank" rel="noopener noreferrer">\n  Get a Free ${selectedEngine.title.replace(' Insurance', '')} Quote\n</a>`}
                preview={
                  <a 
                    href={debouncedLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#2D87F0', textDecoration: 'underline', fontSize: '15px', fontWeight: '500' }}
                  >
                    Get a Free {selectedEngine.title.replace(' Insurance', '')} Quote
                  </a>
                }
              />
              <CodeSnippet
                label="Button"
                description="A styled call-to-action button."
                code={`<a href="${debouncedLink}" target="_blank" rel="noopener noreferrer"\n   style="display:inline-block;padding:12px 28px;background:#2D87F0;color:white;\n          font-weight:600;font-size:15px;border-radius:8px;text-decoration:none;">\n  Get a Free Quote\n</a>`}
                preview={
                  <a 
                    href={debouncedLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '12px 28px',
                      background: '#2D87F0',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '15px',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      transition: 'background 150ms'
                    }}
                    onMouseEnter={e => e.target.style.background = '#1D6FD8'}
                    onMouseLeave={e => e.target.style.background = '#2D87F0'}
                  >
                    Get a Free Quote
                  </a>
                }
              />
              <CodeSnippet
                label="Direct Link URL"
                description="The raw URL with all parameters — for email campaigns, CRM links, or direct sharing."
                code={debouncedLink}
                previewLink={debouncedLink}
              />
              <CodeSnippet
                label="Embedded Quoter (iframe)"
                description="Embed the full quoting experience directly on your page."
                code={`<iframe\n  src="${debouncedLink}"\n  width="100%"\n  height="650"\n  frameborder="0"\n  style="border:none;border-radius:12px;"\n  title="${selectedEngine.title} Quote"\n></iframe>`}
                preview={
                  <div style={{ width: '100%', maxWidth: '600px' }}>
                    <div style={{ 
                      border: '2px solid #DDE7F1', 
                      borderRadius: '12px', 
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                    }}>
                      <iframe
                        src={debouncedLink}
                        width="100%"
                        height="400"
                        frameBorder="0"
                        style={{ border: 'none', display: 'block' }}
                        title={`${selectedEngine.title} Quote Preview`}
                      />
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '8px', textAlign: 'center', fontStyle: 'italic' }}>
                      Live preview — full height will be 650px when embedded
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        )}

      </div>
    </ProtectedLayout>
  );
}
